import { type Dispatch, type SetStateAction, useCallback, useEffect, useState } from 'react';
import type {
  ImportRunView,
  ImportStructure,
  ImportSummary,
  UpdateImportMappingInput,
} from '#shared/types';
import { useMutation } from '#client/hooks/useMutation';
import { ApiError, api } from '#client/lib/api';

import { ENDPOINT } from '#client/lib/endpoints';

/**
 * One import run, and everything the wizard does to it.
 *
 * All four steps read the same object, because they are four views of one thing
 * rather than four screens: changing the header row on step 2 re-stages the
 * rows step 3 previews, and holding that in one hook is what stops the preview
 * and the mapping ever disagreeing.
 *
 * Not built on `useKeyedRequest`, which returns immutable fetched data — the
 * three mutations here replace what was fetched, and re-fetching after each one
 * would throw away a response the server has already computed. It follows the
 * same two rules that hook documents, for the same reasons:
 *
 *   * **`loading` is derived** by comparing the current key against the key of
 *     the last settled result, never toggled with setState inside the effect.
 *   * **A settled result is ignored if its key is stale**, so navigating between
 *     two runs cannot leave one run's mapping rendered against the other's id.
 */

const POLL_MS = 1500;

interface Loaded {
  /** Which request produced this state; compared against the current key. */
  key: string;
  structure: ImportStructure;
  run: ImportRunView;
  summary: ImportSummary | null;
}

export interface ImportRunState {
  structure: ImportStructure | null;
  run: ImportRunView | null;
  summary: ImportSummary | null;
  loading: boolean;
  isBusy: boolean;
  error: string | null;
  updateMapping: (input: UpdateImportMappingInput) => Promise<void>;
  validate: () => Promise<void>;
  commit: () => Promise<void>;
  clearError: () => void;
}

export function useImportRun(id: number | null): ImportRunState {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mutation = useMutation('Something went wrong.');
  const { run: runMutation, reset: resetMutation } = mutation;

  const requestKey = id === null ? undefined : String(id);

  useEffect(() => {
    if (requestKey === undefined || id === null) return;
    let cancelled = false;

    api
      .get<ImportStructure>(ENDPOINT.importStructure(id))
      .then((structure) => {
        if (cancelled) return;
        setLoaded({ key: requestKey, structure, run: structure.run, summary: null });
        setLoadError(null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(messageOf(cause));
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey, id]);

  /** Runs a mutation, and folds its result into the currently visible run. */
  const perform = useCallback(
    async (work: (key: string) => Promise<(previous: Loaded) => Loaded>) => {
      if (requestKey === undefined) return;
      setLoadError(null);
      const outcome = await runMutation(() => work(requestKey));
      if (!outcome.ok) return;
      const apply = outcome.value;
      setLoaded((previous) => (previous?.key === requestKey ? apply(previous) : previous));
    },
    [requestKey, runMutation],
  );

  const actions = useRunActions(perform);
  const visible = loaded !== null && loaded.key === requestKey ? loaded : null;
  useCommitPolling({ id, requestKey, status: visible?.run.status, setLoaded });

  return {
    structure: visible?.structure ?? null,
    run: visible?.run ?? null,
    summary: visible?.summary ?? null,
    loading: requestKey !== undefined && visible === null,
    isBusy: mutation.isPending,
    error: loadError ?? mutation.errorMessage,
    ...actions,
    clearError: useCallback(() => {
      setLoadError(null);
      resetMutation();
    }, [resetMutation]),
  };
}

type Perform = (work: (key: string) => Promise<(previous: Loaded) => Loaded>) => Promise<void>;

/** The three writes the wizard makes to a run. */
function useRunActions(
  perform: Perform,
): Pick<ImportRunState, 'updateMapping' | 'validate' | 'commit'> {
  const updateMapping = useCallback(
    (input: UpdateImportMappingInput) =>
      perform(async (key) => {
        const structure = await api.patch<ImportStructure>(ENDPOINT.importMapping(key), input);
        // The old check described the old mapping. Carrying it over would show
        // counts for a reading of the file that no longer exists.
        return () => ({ key, structure, run: structure.run, summary: null });
      }),
    [perform],
  );

  const validate = useCallback(
    () =>
      perform(async (key) => {
        const summary = await api.post<ImportSummary>(ENDPOINT.importValidate(key), {});
        const run = await api.get<ImportRunView>(ENDPOINT.importRun(key));
        return (previous) => ({ ...previous, run, summary });
      }),
    [perform],
  );

  const commit = useCallback(
    () =>
      perform(async (key) => {
        const run = await api.post<ImportRunView>(ENDPOINT.importCommit(key), {});
        return (previous) => ({ ...previous, run });
      }),
    [perform],
  );

  return { updateMapping, validate, commit };
}

/**
 * Polls only while the write is actually in flight, and stops itself the
 * moment the status settles. A timer left running behind a finished import is
 * a request every second and a half for as long as the tab is open.
 */
function useCommitPolling({
  id,
  requestKey,
  status,
  setLoaded,
}: {
  id: number | null;
  requestKey: string | undefined;
  status: ImportRunView['status'] | undefined;
  setLoaded: Dispatch<SetStateAction<Loaded | null>>;
}) {
  useEffect(() => {
    if (id === null || requestKey === undefined || status !== 'committing') return;

    const timer = window.setInterval(() => {
      // The rejection handler covers the request only — not the state update —
      // and only the failures a request can have: the server's error envelope
      // (`ApiError`) or a dropped connection (`fetch` rejects with `TypeError`).
      // Those are swallowed on purpose: a failed poll is not worth an error
      // banner over a running import, and the next tick either succeeds or the
      // status settles. Anything else is a bug and is rethrown.
      api.get<ImportRunView>(ENDPOINT.importRun(id)).then(
        (run) => {
          setLoaded((previous) => (previous?.key === requestKey ? { ...previous, run } : previous));
        },
        (cause: unknown) => {
          if (cause instanceof ApiError || cause instanceof TypeError) return;
          throw cause;
        },
      );
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [id, requestKey, status, setLoaded]);
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Something went wrong.';
}
